// src/screens/Admin/GraphsDetail.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import './GraphsDetail.css';
import backIcon from '../../assets/backspace-icon.png';
import infoIcon from '../../assets/info-icon.png';

import LineChart from '../../components/charts/LineChart';
import GraphsExplainerDrawer from '../../components/popups/GraphsExplainerDrawer';

function GraphsDetail() {
  const navigate = useNavigate();

  const [series, setSeries] = useState([]);
  const [days, setDays] = useState(30); // number | 'all'
  const [mode, setMode] = useState('daily');

  const [deltas, setDeltas] = useState(null);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState('signups');

  const [winWidth, setWinWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    fetchData(days);
    // eslint-disable-next-line
  }, []);

  const chartWidth = Math.min(1100, Math.max(320, winWidth - 48));

  const fetchData = async (d) => {
    try {
      setDays(d);
      const res = await api.get(`/admin/metrics/timeseries?days=${d}`);
      setSeries(res.data.series || []);
      setMode(res.data.mode || 'daily');
      setDeltas(res.data.deltas || null);
    } catch (err) {
      console.error('Failed to load graphs detail', err);
    }
  };

  const openExplain = (type) => {
    setDrawerType(type);
    setDrawerOpen(true);
  };

  const buildXY = (key) => series.map((d) => ({ x: d.date, y: d[key] }));

  const buildStickiness = () =>
    series.map((d) => ({
      x: d.date,
      y: d.active7d > 0 ? Math.round((d.active24h / d.active7d) * 100) : 0,
    }));

  const formatXShort = useMemo(() => {
    return (d) => (typeof d === 'string' ? d.slice(5) : d);
  }, []);

  const InfoBtn = ({ onClick, label }) => (
    <button
      type="button"
      onClick={onClick}
      className="graphs-infoBtn"
      aria-label={label}
      title={label}
    >
      <img src={infoIcon} alt="" className="graphs-infoIcon" />
    </button>
  );

  // ✅ Build the neutral compare text (no "vs", no short "7d")
  const compareText =
    deltas?.windowLabel ||
    (days === 'all'
      ? (mode === 'weekly' ? 'previous 4 weeks' : 'previous 30 days')
      : `previous ${days} days`);

  // ✅ Subtitle builder:
  // - only the % is colored
  // - the "• from previous X days" stays neutral
  const DeltaLine = ({ statKey }) => {
    const s = deltas?.[statKey];
    if (!s || deltas?.ok === false) return null;

    // New (prev = 0 but current > 0)
    if (s.isNew) {
      return (
        <span className="graphs-delta">
          <span className="graphs-delta-pct graphs-delta-new">New</span>
          <span className="graphs-delta-sub"> • from {compareText}</span>
        </span>
      );
    }

    const pct = Number(s.pct);
    const isPos = Number.isFinite(pct) && pct > 0;
    const isNeg = Number.isFinite(pct) && pct < 0;

    const pctText = Number.isFinite(pct)
      ? `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`
      : '0.0%';

    return (
      <span className="graphs-delta">
        <span
          className={[
            'graphs-delta-pct',
            isPos ? 'graphs-delta-pos' : '',
            isNeg ? 'graphs-delta-neg' : '',
          ].join(' ')}
        >
          {pctText}
        </span>
        <span className="graphs-delta-sub"> • from {compareText}</span>
      </span>
    );
  };

  return (
    <div className="graphs-page">
      <div className="graphs-header">
        <img
          src={backIcon}
          alt="Back"
          className="graphs-back"
          onClick={() => navigate('/admin/graphs')}
        />
        <span className="graphs-title">Detailed Graphs</span>
      </div>

      <div className="graphs-scroll">
        <div className="graphs-filters">
          <button className={days === 7 ? 'active' : ''} onClick={() => fetchData(7)}>7d</button>
          <button className={days === 30 ? 'active' : ''} onClick={() => fetchData(30)}>30d</button>
          <button className={days === 90 ? 'active' : ''} onClick={() => fetchData(90)}>90d</button>
          <button className={days === 'all' ? 'active' : ''} onClick={() => fetchData('all')}>All</button>
        </div>

        <div className="graphs-rows">
          <LineChart
            title="Signups"
            subtitle={<DeltaLine statKey="signups" />}
            titleRight={
              <InfoBtn
                onClick={() => openExplain('signups')}
                label="What does Signups mean?"
              />
            }
            data={buildXY('signups')}
            width={chartWidth}
            height={240}
            formatX={formatXShort}
            tooltipFormat={({ x, y }) => ({ title: `Date: ${x}`, lines: [`Signups: ${y}`] })}
          />

          <LineChart
            title="Active Users (24h) — DAU"
            subtitle={<DeltaLine statKey="dau" />}
            titleRight={
              <InfoBtn
                onClick={() => openExplain('dau')}
                label="What does DAU mean?"
              />
            }
            data={buildXY('active24h')}
            width={chartWidth}
            height={240}
            formatX={formatXShort}
            tooltipFormat={({ x, y }) => ({ title: `Date: ${x}`, lines: [`DAU: ${y}`] })}
          />

          <LineChart
            title={mode === 'weekly' ? 'Active Users (4w Rolling) — WAU' : 'Active Users (7d Rolling) — WAU'}
            subtitle={<DeltaLine statKey="wau" />}
            titleRight={
              <InfoBtn
                onClick={() => openExplain('wau')}
                label="What does WAU mean?"
              />
            }
            data={buildXY('active7d')}
            width={chartWidth}
            height={240}
            formatX={formatXShort}
            tooltipFormat={({ x, y }) => ({
              title: `Date: ${x}`,
              lines: [mode === 'weekly' ? `WAU (4w rolling): ${y}` : `WAU (7d rolling): ${y}`],
            })}
          />

          <LineChart
            title="Stickiness (DAU / WAU %)"
            subtitle={<DeltaLine statKey="stickiness" />}
            titleRight={
              <InfoBtn
                onClick={() => openExplain('stickiness')}
                label="What does Stickiness mean?"
              />
            }
            data={buildStickiness()}
            width={chartWidth}
            height={240}
            yMax={100}
            formatX={formatXShort}
            formatY={(v) => `${v}%`}
            tooltipFormat={({ x, y }) => ({ title: `Date: ${x}`, lines: [`DAU/WAU: ${y}%`] })}
          />
        </div>
      </div>

      <GraphsExplainerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        type={drawerType}
      />
    </div>
  );
}

export default GraphsDetail;
